import { useState } from "react";
import { useTemplates } from "@/hooks/useData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { widgetMeta } from "@/lib/widgets";
import { cn } from "@/lib/utils";
import { Sparkles, SquareDashed } from "lucide-react";

function TemplatePreview({ layout }) {
  // tiny visual map of the template's grid
  const maxY = Math.max(...layout.map((l) => l.y + l.h), 1);
  return (
    <div className="relative h-24 w-full overflow-hidden rounded-lg border bg-surface-2 p-1">
      {layout.map((l) => (
        <div
          key={l.i}
          className="absolute rounded-[3px] bg-primary/25 ring-1 ring-primary/30"
          style={{
            left: `${(l.x / 12) * 100}%`,
            top: `${(l.y / maxY) * 100}%`,
            width: `calc(${(l.w / 12) * 100}% - 3px)`,
            height: `calc(${(l.h / maxY) * 100}% - 3px)`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Starter templates - like picking a Canva/Notion template instead of a blank page.
 * Every widget can still be moved, resized or deleted afterwards.
 */
export default function TemplatePicker({ onCreate, creating }) {
  const { data: templates = [] } = useTemplates();
  const [selected, setSelected] = useState(null); // null = blank
  const [name, setName] = useState("My Workspace");

  return (
    <div className="w-full" data-testid="template-picker">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {templates.map((t) => (
          <button
            key={t.template_id}
            onClick={() => { setSelected(t.template_id); if (name === "My Workspace") setName(t.name); }}
            className={cn(
              "group rounded-2xl border bg-card p-4 text-left transition-all duration-150 hover:shadow-[0_10px_30px_rgba(2,6,23,0.10)]",
              selected === t.template_id && "ring-2 ring-ring"
            )}
            data-testid={`template-card-${t.template_id}`}
          >
            <TemplatePreview layout={t.layout} />
            <div className="mt-3 flex items-center gap-2">
              <span className="font-display text-sm font-semibold">{t.name}</span>
              <Badge variant="secondary" className="text-[9px]">{t.layout.length} widgets</Badge>
            </div>
            <p className="mt-0.5 text-[11px] font-medium text-primary">{t.tagline}</p>
            <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-muted-foreground">{t.description}</p>
            <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">{t.persona}</p>
          </button>
        ))}
        <button
          onClick={() => setSelected(null)}
          className={cn(
            "flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed bg-card/50 p-4 text-center transition-all duration-150 hover:bg-muted/40",
            selected === null && "ring-2 ring-ring"
          )}
          data-testid="template-card-blank"
        >
          <SquareDashed className="h-6 w-6 text-muted-foreground" />
          <span className="font-display text-sm font-semibold">Start blank</span>
          <span className="text-[11px] text-muted-foreground">An empty canvas. Drag in exactly what you want.</span>
        </button>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Workspace name"
          className="h-9 sm:max-w-xs" data-testid="workspace-name-input" />
        <Button onClick={() => onCreate(name, selected)} disabled={creating} data-testid="create-workspace-button">
          <Sparkles className="mr-1.5 h-4 w-4" />
          {creating ? "Creating…" : "Create workspace"}
        </Button>
      </div>
    </div>
  );
}
